import { useCallback, useContext, useEffect, useState } from 'react';
import { Modal, Select, Form, Divider, Skeleton } from 'antd';
import { useIntl } from 'react-intl';
import { mediaHook } from 'customHooks';
import classnames from 'classnames';

import useFacebookIntegration, {
  FacebookIntegrationProvider
} from 'contexts/loginToMeta.context';
import { initialName } from 'utils/helpers';
import { LOCALE } from 'constants/intl-wrapper';
import { INSTAGRAM } from 'constants/chats';
import {
  CONVERSATIONAL_SALES_HELP_ARTICLE,
  CONVERSATIONAL_SALES_VIDEO_AND_ARTICLE
} from 'constants/pixels';

import BRButton from 'components/BRButton/BRButton';
import WebHookModal from 'components/Pixels/components/ConversationalCommerce/components/WebhookModal/WebHookModal';
import DisconnectModal from 'components/Pixels/components/ConversationalCommerce/components/DisconnectModal/DisconnectModal';
import SelectPageEmptyState from '../SelectPageEmptyState/SelectPageEmptyState';

import { ReactComponent as ArrowLeft } from 'assets/bosta-icons/Arrow-left.svg';
import { ReactComponent as RightIcon } from 'assets/bosta-icons/Arrow-right.svg';
import { ReactComponent as DisconnectIcon } from 'assets/bosta-icons/disconnect-icon.svg';
import { ReactComponent as Warning } from 'assets/bosta-icons/alert-circle.svg';

import './SelectBusinessAndPageModal.less';

const FINAL_STEP = 3;

const SelectBusinessAndPageModalWithContext = ({
  close,
  cb,
  platform,
  mobileScreenSizes: { isSmallMobileScreen },
  isConnected,
  handleDisconnection,
  handleLoginToMeta,
  currentPlatform,
  setLoggedPlatform,
  ...props
}) => {
  //hooks
  const { formatMessage, locale } = useIntl();
  const [step, setStep] = useState(0);
  const [selectedMessengerBusiness, setSelectedMessengerBusiness] =
    useState(null);
  const [selectedMessengerPage, setSelectedMessengerPage] = useState(null);
  const [selectedWhatsAppBusiness, setSelectedWhatsAppBusiness] =
    useState(null);
  const { businessAndPagesData, selectedBusinessAndPagesData, isFetchingData } =
    useContext(useFacebookIntegration);

  //variables
  const selectionKey =
    typeof platform === 'object' ? platform.keyName : platform;

  const targetSelectedPlatform =
    selectedBusinessAndPagesData && selectedBusinessAndPagesData[selectionKey];

  const selectedMessengerBusisnessData = businessAndPagesData?.find(
    (business) => business.businessId === selectedMessengerBusiness
  );
  const selectedWhatsappBussinessData = businessAndPagesData?.find(
    (business) => business.businessId === selectedWhatsAppBusiness
  );

  const connectedBusiness =
    businessAndPagesData &&
    targetSelectedPlatform &&
    businessAndPagesData.find((item) =>
      item.pages.some(
        (page) => page.pageName === targetSelectedPlatform.pageName
      )
    );

  const connectedPage = connectedBusiness?.pages?.find(
    (item) => item.pageName === targetSelectedPlatform.pageName
  );

  const articleData =
    CONVERSATIONAL_SALES_VIDEO_AND_ARTICLE[currentPlatform.key.toUpperCase()][
      CONVERSATIONAL_SALES_HELP_ARTICLE
    ];

  //methods
  const handleModalAction = useCallback(
    (key) => {
      if (!step) {
        if (cb) {
          cb({
            metaBusinessId: selectedMessengerBusiness,
            pageId: selectedMessengerPage
          });
        }
        close();
      } else {
        setStep(1);
      }
    },
    [step, cb, close, selectedMessengerBusiness, selectedMessengerPage]
  );

  const handleBackStep = () => {
    setStep(step - 1);
  };

  const arrowBasedOnLanguage =
    step !== FINAL_STEP &&
    (locale === LOCALE.EN ? (
      <ArrowLeft onClick={handleBackStep} />
    ) : (
      <RightIcon onClick={handleBackStep} />
    ));

  const handleOpenConnectionModal = () => {
    const { configId: currentConfigId, keyName: currentKeyName } =
      JSON.parse(localStorage.getItem('currentPlugin')) || {};

    const { configId: platformConfigId, keyName: platformKeyName } =
      platform || {};

    if (window.FB && window.FB.logout) {
      window.FB.logout();
    }
    close();
    handleLoginToMeta(
      platformConfigId || currentConfigId,
      platformKeyName || currentKeyName
    );
  };

  const handleDisconnectPlugin = (platform) => {
    close();
    handleDisconnection(platform.keyName.toUpperCase());
  };

  const handleFilterOptions = (input, option) => {
    return option.children.toLowerCase().includes(input.toLowerCase());
  };

  //effects
  useEffect(() => {
    if (businessAndPagesData?.length === 1) {
      setSelectedMessengerBusiness(businessAndPagesData[0]?.businessId);
      if (businessAndPagesData[0]?.pages?.length === 1) {
        setSelectedMessengerPage(businessAndPagesData[0]?.pages[0]?.pageId);
      }
    }
  }, [
    businessAndPagesData,
    selectedMessengerBusiness,
    selectedMessengerPage,
    targetSelectedPlatform?.pageId,
    isConnected
  ]);

  const platformNameMapper = (Platform) => {
    switch (Platform) {
      case INSTAGRAM:
        return 'chats_plugins.install_plugins.instagram';
      default:
        return 'chats_plugins.install_plugins.messenger';
    }
  };

  useEffect(() => {
    setSelectedMessengerBusiness(
      connectedBusiness?.businessId || targetSelectedPlatform?.businessManagerId
    );
    setSelectedMessengerPage(connectedPage?.pageId);
  }, [
    connectedBusiness?.businessId,
    connectedPage?.pageId,
    targetSelectedPlatform?.businessManagerId
  ]);

  const RenderModalFooter = (
    <div
      className={`br-select-business-page__footer-modal-container-${
        step === FINAL_STEP ? 'end' : step || isConnected ? 'between' : 'end'
      }`}
    >
      {step !== FINAL_STEP && isConnected && (
        <BRButton
          type="destructive-treitary"
          onClick={() => setStep(FINAL_STEP)}
          label={formatMessage({
            id: 'chats_plugins.install_plugins.disconnect'
          })}
          prefixIcon={<DisconnectIcon />}
          className="br-select-business-page__modal-footer-disconnect-plugin"
        />
      )}
      <div className="br-select-business-page__modal-footer">
        <BRButton
          label={formatMessage({ id: 'common.cancel' })}
          type="outline"
          onClick={close}
        />
        {step === FINAL_STEP ? (
          <BRButton
            type="destructive-primary"
            label={formatMessage({
              id: 'chats_plugins.install_plugins.disconnect_app'
            })}
            onClick={() => handleDisconnectPlugin(platform)}
          />
        ) : (
          <BRButton
            label={formatMessage({
              id: `common.${!step ? 'done' : 'finish'}`
            })}
            onClick={() => handleModalAction(currentPlatform?.key)}
            type="primary"
            disabled={!selectedMessengerBusiness || !selectedMessengerPage}
          />
        )}
      </div>
    </div>
  );

  return (
    <Modal
      title={
        <div className="br-select-business__title-container">
          <div>
            {!!step && arrowBasedOnLanguage}
            <div className="br-select-business__description-container">
              {step === FINAL_STEP ? (
                <span className="display-xs">
                  {formatMessage({ id: `${currentPlatform?.disconnectTitle}` })}
                </span>
              ) : (
                <>
                  <span className="display-xs">
                    {formatMessage({
                      id: `${currentPlatform?.title}`
                    })}
                  </span>
                  <span className="body text-gray">
                    {formatMessage({
                      id: `chats_plugins.install_plugins.${
                        !!step
                          ? currentPlatform?.add_webHook
                          : currentPlatform?.select_page
                      }`
                    })}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      }
      width={null}
      className="br-select-business-page__modal"
      footer={RenderModalFooter}
      onCancel={close}
      {...props}
    >
      {!step ? (
        <Skeleton
          loading={isFetchingData && !businessAndPagesData?.length}
          size="large"
          paragraph={{
            rows: 6
          }}
          active
        >
          <div className="br-select-buisiness-page__container">
            {!businessAndPagesData?.length ? (
              <SelectPageEmptyState />
            ) : (
              <div>
                <div className="br-select-buisiness-page__form-container">
                  <Form.Item
                    label={formatMessage({
                      id: 'chats_plugins.install_plugins.business_manager'
                    })}
                  >
                    <Select
                      filterOption={handleFilterOptions}
                      getPopupContainer={(trigger) => trigger.parentElement}
                      onChange={(value) => {
                        setSelectedMessengerBusiness(value);
                        setSelectedMessengerPage(null);
                      }}
                      value={selectedMessengerBusiness || null}
                      placeholder={formatMessage({
                        id: 'chats_plugins.install_plugins.select_your_business'
                      })}
                    >
                      {businessAndPagesData?.map((business) => {
                        const { businessId, businessName, pages } = business;
                        return (
                          <Select.Option value={businessId} key={businessId}>
                            <div className="br-select-business-page__container">
                              <span className="br-select-business-page__avatar">
                                {initialName(business, 'businessName', true)}
                              </span>
                              {businessName}
                            </div>
                          </Select.Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                  <Form.Item
                    label={formatMessage({
                      id: `${currentPlatform?.page}`
                    })}
                  >
                    <Select
                      filterOption={handleFilterOptions}
                      getPopupContainer={(trigger) => trigger.parentElement}
                      value={selectedMessengerPage || null}
                      onChange={setSelectedMessengerPage}
                      placeholder={formatMessage({
                        id: 'chats_plugins.install_plugins.select_fb_page'
                      })}
                    >
                      {selectedMessengerBusisnessData?.pages?.map(
                        (page) =>
                          (!page?.platform ||
                            currentPlatform.key === page?.platform) && (
                            <Select.Option
                              value={page.pageId}
                              key={page.pageId}
                            >
                              <div className="br-select-business-page__container">
                                <span className="br-select-business-page__avatar">
                                  {initialName(page, 'pageName', true)}
                                </span>
                                {page.pageName}
                              </div>
                            </Select.Option>
                          )
                      )}
                    </Select>
                  </Form.Item>
                </div>
              </div>
            )}

            {selectedWhatsAppBusiness && (
              <>
                <Divider />
                <div>
                  <span className="display-xs">WhatsApp Configurations</span>
                  <div>
                    <Form.Item
                      label={formatMessage({
                        id: 'chats_plugins.install_plugins.select_your_business'
                      })}
                    >
                      <Select
                        showSearch
                        filterOption={handleFilterOptions}
                        getPopupContainer={(trigger) => trigger.parentElement}
                        onChange={(value) => {
                          setSelectedMessengerBusiness(value);
                          setSelectedMessengerPage(null);
                        }}
                        value={selectedWhatsappBussinessData?.businessId}
                      >
                        {businessAndPagesData?.map(
                          ({ businessId, businessName, whatsappNumbers }) => {
                            return (
                              !!whatsappNumbers.length && (
                                <Select.Option
                                  value={businessId}
                                  key={businessId}
                                >
                                  {businessName}
                                </Select.Option>
                              )
                            );
                          }
                        )}
                      </Select>
                    </Form.Item>
                    <Form.Item label="Select Whatsapp Phone">
                      <Select
                        showSearch
                        filterOption={(input, option) => {
                          return option.children
                            .toLowerCase()
                            .includes(input.toLowerCase());
                        }}
                        getPopupContainer={(trigger) => trigger.parentElement}
                        value={
                          selectedWhatsappBussinessData?.whatsappNumbers[0]
                        }
                        disabled
                      >
                        {selectedWhatsappBussinessData?.whatsappNumbers?.map(
                          (number) => (
                            <Select.Option value={number} key={number}>
                              {number}
                            </Select.Option>
                          )
                        )}
                      </Select>
                    </Form.Item>
                  </div>
                </div>
              </>
            )}
            <div>
              <div
                className={classnames(
                  'br-select-business-page__container-permissions',
                  {
                    'br-select-business-page__container-permissions-mobile':
                      isSmallMobileScreen
                  }
                )}
              >
                <p className="br-select-business-page__container-permissions-title">
                  {formatMessage({
                    id: 'chats_plugins.install_plugins.facing_any_problems'
                  })}
                </p>
                <BRButton
                  label={formatMessage({
                    id: 'chats_plugins.install_plugins.review_permissions'
                  })}
                  className={'button-md'}
                  onClick={handleOpenConnectionModal}
                />
              </div>
              <div className="br-conversational-sales-card__support-section">
                <span>
                  <span>
                    <Warning />
                  </span>
                  <div className="body-medium">
                    {formatMessage({
                      id: `chats_plugins.install_plugins.very_important_step`
                    })}
                    <div className="br-conversational-sales-card__support-section__description body">
                      {!!articleData ? (
                        <>
                          {formatMessage(
                            {
                              id: `chats_plugins.install_plugins.help_article`
                            },
                            {
                              link: (
                                <a
                                  className="br-conversational-sales-card__support-section__help-article"
                                  href={articleData[locale]}
                                >
                                  {formatMessage({
                                    id: 'pixels.add_pixel_modal.help_article'
                                  })}
                                </a>
                              ),
                              platform: (
                                <span>
                                  {formatMessage({
                                    id: platformNameMapper(currentPlatform?.key)
                                  })}
                                </span>
                              )
                            }
                          )}
                        </>
                      ) : (
                        formatMessage({
                          id: `chats_plugins.install_plugins.without_adding_webhook`
                        })
                      )}
                    </div>
                  </div>
                </span>
              </div>
            </div>
          </div>
        </Skeleton>
      ) : (
        <DisconnectModal currentPlatform={currentPlatform} />
      )}
    </Modal>
  );
};

const SelectBusinessAndPageModal = (props) => {
  return (
    <FacebookIntegrationProvider>
      <SelectBusinessAndPageModalWithContext {...props} />
    </FacebookIntegrationProvider>
  );
};

export default mediaHook(SelectBusinessAndPageModal);
